/**
 * Blog Posts Query Hook
 *
 * Fetch and manage blog posts list with React Query.
 * Provides loading, error, and data states.
 *
 * @example
 * const { data: posts, isLoading, error } = useBlogPosts();
 */

import { useQuery } from '@tanstack/react-query';
import { fetchBlogPosts, fetchBlogPostById } from '../api';
import type { BlogPost } from '../types';

/**
 * Query key factory for blog posts
 * Use these keys for cache invalidation
 */
export const blogPostsKeys = {
  all: ['blog_posts'] as const,
  detail: (id: string) => ['blog_posts', id] as const,
};

/**
 * Hook to fetch all blog posts
 * @returns React Query result with posts array
 */
export const useBlogPosts = () => {
  return useQuery<BlogPost[], Error>({
    queryKey: blogPostsKeys.all,
    queryFn: fetchBlogPosts,
  });
};

/**
 * Hook to fetch a single blog post by ID
 * @param id - Blog post ID
 * @returns React Query result with single post
 */
export const useBlogPost = (id: string | undefined) => {
  return useQuery<BlogPost, Error>({
    queryKey: blogPostsKeys.detail(id || ''),
    queryFn: () => fetchBlogPostById(id!),
    enabled: !!id,
  });
};
